'use client';

import {
  Alert,
  Box,
  Button,
  Flex,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalHeader,
  SpriteIcon,
  Typography
} from '@coolblue-development/freddy';
import focusInputTrigger from '@coolblue-development/ga4-events/triggers/ActiveMailer/focusInputTrigger';
import openOverlayTrigger from '@coolblue-development/ga4-events/triggers/ActiveMailer/openOverlayTrigger';
import readPrivacyPolicyTrigger from '@coolblue-development/ga4-events/triggers/ActiveMailer/readPrivacyPolicyTrigger';
import subscribeTrigger from '@coolblue-development/ga4-events/triggers/ActiveMailer/subscribeTrigger';
import unsubscribeTrigger from '@coolblue-development/ga4-events/triggers/ActiveMailer/unsubscribeTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { OutOfStockButton } from '@coolblue-development/next-patterns/client';
import type { Product } from '@features/product/typings';
import { MainProduct } from '@features/second-chance/typings';
import { useTranslation } from '@i18n/useTranslation';
import { useState } from 'react';

import { createActiveMail, deleteActiveMail } from './actions';
import { useActiveMailerContext } from './ActiveMailerContext';
import { validateEmailField } from './utils';

type Props = {
  product: Product | MainProduct;
  privacyPolicyUrl?: string;
  feature?: 'order_block' | 'bottom_product_card';
};

export function ActiveMailerButton({ product, privacyPolicyUrl, feature = 'order_block' }: Props) {
  const { actionState, setActionState } = useActiveMailerContext();
  const { t } = useTranslation('webshop.activeMailer');
  const modalTranslations = useFreddyTranslations('modal');
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  const { trackEvent } = useAnalytics();
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  function renderModalTitle() {
    switch (actionState.state) {
      case 'initial':
        return t('modal.title');
      case 'subscribed':
        return t('modal.subscribedConfirmationTitle');
      case 'unsubscribed':
        return t('modal.unsubscribedConfirmationTitle');
    }
  }

  function openModal() {
    setOpen(true);

    trackEvent(
      openOverlayTrigger({
        sub_feature: feature
      })
    );
  }

  function closeModal() {
    setOpen(false);
  }

  async function onSubmitSubscribeForm(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // To not propagate the event to the OrderForm.
    event.stopPropagation();

    const emailAddress = new FormData(event.currentTarget).get('email') as string;
    const validationResult = validateEmailField(emailAddress);

    setEmailError(validationResult?.errorType);

    if (!validationResult.isValid) return;

    setPending(true);
    setError(false);

    try {
      const { data } = await createActiveMail({ productId: product.id, emailAddress });

      setActionState({ state: 'subscribed', activeMailId: data.id, emailAddress });

      trackEvent(
        subscribeTrigger({
          item_id: product.id,
          status: 'success'
        })
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      setError(true);

      trackEvent(
        subscribeTrigger({
          item_id: product.id,
          status: 'failure'
        })
      );
    } finally {
      setPending(false);
    }
  }

  async function onUnsubscribeClick() {
    if (pending) return;
    if (actionState.state !== 'subscribed') return;

    setPending(true);
    setError(false);

    try {
      await deleteActiveMail({ activeMailId: actionState.activeMailId });

      setActionState({ state: 'unsubscribed' });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      setError(true);
    } finally {
      setPending(false);

      trackEvent(unsubscribeTrigger());
    }
  }

  function onPrivacyPolicyLinkClick() {
    trackEvent(
      readPrivacyPolicyTrigger({
        subject_id: t('privacyPolicyLinkLabel')
      })
    );
  }

  function onEmailAddressFieldFocus() {
    trackEvent(
      focusInputTrigger({
        input_field: 'email',
        // Update status value when field validation is implemented
        status: 'success'
      })
    );
  }

  function onEmailAddressFieldBlur(event: React.FocusEvent<HTMLInputElement>) {
    const result = validateEmailField(event.target.value);
    setEmailError(result?.errorType);
  }

  function getEmailErrorText() {
    if (!emailError) return;

    switch (emailError) {
      case 'required':
        return t('emailAddressField.requiredError');
      case 'invalid':
        return t('emailAddressField.invalidError');
      default:
        return;
    }
  }

  return (
    <>
      <OutOfStockButton isFullWidth label={t('subscribeButtonLabel')} onClick={openModal} />
      <Modal
        open={open}
        size='medium'
        onClose={closeModal}
        translations={modalTranslations}
        placement={{
          xs: 'bottom',
          md: 'center'
        }}
        header={
          <ModalHeader
            title={renderModalTitle()}
            closeButtonProps={{
              'aria-label': t('modal.closeButtonLabel')
            }}
          />
        }
        body={
          <ModalBody>
            {actionState.state === 'initial' && (
              <form onSubmit={onSubmitSubscribeForm}>
                <Typography mb={200}>{t('modal.description')}</Typography>

                {error && <Alert intent='error' description={t('generalErrorText')} mb={200} />}

                <Input
                  label={t('emailAddressField.label')}
                  name='email'
                  type='email'
                  onFocus={onEmailAddressFieldFocus}
                  onBlur={onEmailAddressFieldBlur}
                  validation={emailError ? 'error' : undefined}
                  helperText={getEmailErrorText()}
                />

                <Flex direction='column' mt={800} gap={200}>
                  <Button alignSelf='flex-start' type='submit' loading={pending}>
                    {t('subscribeButtonLabel')}
                  </Button>

                  <Link
                    alignSelf='flex-start'
                    href={privacyPolicyUrl}
                    onClick={onPrivacyPolicyLinkClick}
                  >
                    {t('privacyPolicyLinkLabel')}
                  </Link>
                </Flex>
              </form>
            )}

            {actionState.state === 'subscribed' && (
              <Flex direction='column' gap={400}>
                <Flex gap={200}>
                  <Box flexShrink={0} color='sentimentPositive'>
                    <SpriteIcon name='checkmark-circled' size='large' innerFill='contrast' />
                  </Box>

                  <Typography>
                    {t('modal.subscribedConfirmationText', {
                      productName: product.name,
                      emailAddress: actionState.emailAddress
                    })}
                  </Typography>
                </Flex>

                <Flex direction='column' gap={200}>
                  <Typography variant='h5' mb={0} component='p'>
                    {t('modal.unsubscribePrompt')}
                  </Typography>

                  <Link
                    component='button'
                    startIcon={<SpriteIcon name='arrow-right' />}
                    onClick={onUnsubscribeClick}
                  >
                    {t('unsubscribeButtonLabel')}
                  </Link>
                </Flex>

                {error && <Alert intent='error' description={t('generalErrorText')} />}
              </Flex>
            )}

            {actionState.state === 'unsubscribed' && (
              <Typography>
                {t('modal.unsubscribedConfirmationText', {
                  productName: product.name
                })}
              </Typography>
            )}
          </ModalBody>
        }
      />
    </>
  );
}
